import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useEmployees, useServices } from '../hooks/useUniverseApi';
import { useAuthStore } from '@shared/store/authStore';

type ServiceCategory = 'gym' | 'pool' | 'tennis' | 'padel' | 'beauty' | 'restaurant' | 'beach';

const categoryConfig = {
  gym: {
    name: 'Тренажерный зал',
    icon: '🏋️',
    color: 'bg-orange-500',
    description: 'Персональные тренировки и групповые занятия'
  },
  pool: {
    name: 'Бассейн',
    icon: '🏊',
    color: 'bg-blue-500',
    description: 'Плавание и аква-аэробика'
  },
  tennis: {
    name: 'Теннис',
    icon: '🎾',
    color: 'bg-green-500',
    description: 'Теннисные корты и уроки'
  },
  padel: {
    name: 'Падел',
    icon: '🏓',
    color: 'bg-teal-500',
    description: 'Падел корты и турниры'
  },
  beauty: {
    name: 'Бьюти салон',
    icon: '💆‍♀️',
    color: 'bg-pink-500',
    description: 'Массаж, уходы для волос, стрижки'
  },
  restaurant: {
    name: 'Ресторан',
    icon: '🍽️',
    color: 'bg-yellow-500',
    description: 'Здоровое питание и напитки'
  },
  beach: {
    name: 'Пляжный клуб',
    icon: '🏖️',
    color: 'bg-cyan-500',
    description: 'Отдых у воды и пляжные активности'
  }
};

const allCategories = Object.keys(categoryConfig) as ServiceCategory[];

export function ServicesPage() {
  const { category } = useParams<{ category?: ServiceCategory }>();
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(category || null);

  const { data: employees, isLoading: employeesLoading } = useEmployees();
  const { data: services, isLoading: servicesLoading } = useServices();

  // Process real API data into categories
  const getServicesByCategory = (category: ServiceCategory) => {
    if (!employees || !services) return [];
    
    const categoryEmployees = employees.filter(emp => {
      const specName = emp.spec_name?.toLowerCase() || '';
      const depName = emp.department?.dep_name?.toLowerCase() || '';
      
      switch (category) {
        case 'tennis':
        case 'padel':
          return specName.includes('теннис') || specName.includes('администратор теннис');
        case 'gym':
          return depName.includes('тренажерный') || specName.includes('тренер тренажерного');
        case 'beauty':
          return depName.includes('beauty') || specName.includes('визажист') || specName.includes('бровист');
        default:
          return false;
      }
    });

    const categoryServices: any[] = [];
    
    categoryEmployees.forEach(emp => {
      if (emp.services && emp.services.length > 0) {
        emp.services.forEach((service: any) => {
          categoryServices.push({
            id: service.service_id,
            name: service.service_name,
            price: service.service_price,
            duration: service.duration || 60,
            employee: `${emp.first_name} ${emp.last_name}`,
            employeeId: emp.Id,
            isGroup: service.is_group === 1
          });
        });
      } else {
        // For employees without specific services, create generic service
        if (category === 'tennis' || category === 'padel') {
          categoryServices.push({
            id: `emp_${emp.Id}`,
            name: category === 'tennis' ? 'Урок тенниса' : 'Урок падел',
            price: 2500,
            duration: 60,
            employee: `${emp.first_name} ${emp.last_name}`,
            employeeId: emp.Id,
            isGroup: false
          });
        } else if (category === 'gym') {
          categoryServices.push({
            id: `emp_${emp.Id}`,
            name: 'Персональная тренировка',
            price: 2500,
            duration: 60,
            employee: `${emp.first_name} ${emp.last_name}`,
            employeeId: emp.Id,
            isGroup: false
          });
        }
      }
    });

    return categoryServices;
  };

  const currentServices = selectedCategory ? getServicesByCategory(selectedCategory) : [];

  if (employeesLoading || servicesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-600">Загружаем услуги...</p>
        </div>
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Наши услуги</h1>
          <p className="text-gray-600">Выберите интересующую вас категорию</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {allCategories.map((cat) => {
            const config = categoryConfig[cat];
            return (
              <Link
                key={cat}
                to={`/services/${cat}`}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-16 h-16 ${config.color} rounded-2xl flex items-center justify-center mb-4 text-white text-2xl`}>
                  {config.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{config.name}</h3>
                <p className="text-sm text-gray-600">{config.description}</p>
              </Link>
            );
          })}
        </div>

        {/* Popular Services from real data */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Популярные услуги</h2>
          <div className="space-y-4">
            {employees && employees.slice(0, 2).map((emp, idx) => {
              if (!emp.services || emp.services.length === 0) return null;
              const service = emp.services[0];
              const categoryIcon = emp.department?.dep_name?.includes('beauty') ? '💄' : 
                                 emp.department?.dep_name?.includes('Тренажерный') ? '🏋️' : '🎾';
              const categoryColor = emp.department?.dep_name?.includes('beauty') ? 'bg-pink-500' : 
                                   emp.department?.dep_name?.includes('Тренажерный') ? 'bg-orange-500' : 'bg-green-500';
              
              return (
                <div key={emp.Id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 ${categoryColor} rounded-lg flex items-center justify-center text-white`}>
                      {categoryIcon}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{service.service_name}</h4>
                      <p className="text-sm text-gray-600">{service.duration || 60} мин • {service.service_price} ₽</p>
                    </div>
                  </div>
                  <Link 
                    to={`/booking/${service.service_id}`} 
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium"
                  >
                    Записаться
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const config = categoryConfig[selectedCategory];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/services" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 ${config.color} rounded-xl flex items-center justify-center text-white text-xl`}>
            {config.icon}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{config.name}</h1>
            <p className="text-gray-600">{config.description}</p>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div className="space-y-4">
        {currentServices.map((service) => (
          <div key={service.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">{service.name}</h3>
                {service.employee && (
                  <p className="text-gray-600">Мастер: {service.employee}</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-emerald-600">{service.price} ₽</div>
                <div className="text-sm text-gray-500">{service.duration} мин</div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Link 
                to={`/booking/${service.id}`}
                className="flex-1 bg-emerald-600 text-white py-3 px-6 rounded-lg font-medium text-center hover:bg-emerald-700 transition-colors"
              >
                Записаться
              </Link>
              <button className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                Подробнее
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Category Features */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Особенности</h2>
        <div className="grid grid-cols-2 gap-4">
          {selectedCategory === 'gym' && (
            <>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-orange-600">⚡</span>
                </div>
                <span className="text-sm text-gray-700">Современное оборудование</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-orange-600">🎯</span>
                </div>
                <span className="text-sm text-gray-700">Персональные программы</span>
              </div>
            </>
          )}
          {selectedCategory === 'beauty' && (
            <>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                  <span className="text-pink-600">✨</span>
                </div>
                <span className="text-sm text-gray-700">Премиум косметика</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                  <span className="text-pink-600">🌿</span>
                </div>
                <span className="text-sm text-gray-700">Натуральные процедуры</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}